// Condition and out of service on the web (CR_20260921_Asset_Lifecycle T2). The latest condition is on
// asset_overview; the few reports before it come from asset_condition_reports (read like the asset). Both are written
// only by RPCs: report_condition (anyone who can see the asset, an optional photo in asset-docs/<co>/condition/) and
// set_service_state (a manager, or warehouse staff at the asset's site — §4.5).
import {
  type AssetCondition,
  type ConditionSource,
  DOC_BUCKET,
  docPath,
} from '@fleetspoke/core/lifecycle';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { downscaleImage } from '../lib/images';
import { supabase } from '../lib/supabase';
import { lifecycleKeys } from './lifecycle';
import { keys } from './queries';

export interface ConditionReport {
  id: number;
  condition: AssetCondition;
  source: ConditionSource;
  at: string;
  by_user: string | null;
  note: string | null;
}

export const conditionKeys = { asset: (assetId: string) => ['condition', assetId] as const };

/** The last `n` reports, newest first, with who made them. */
export function useConditionReports(assetId: string, n = 4) {
  return useQuery({
    queryKey: [...conditionKeys.asset(assetId), n],
    enabled: !!assetId,
    queryFn: async () => {
      const { data, error } = await supabase()
        .from('asset_condition_reports')
        .select('id, condition, source, at, by_user, note')
        .eq('asset_id', assetId)
        .order('at', { ascending: false })
        .order('id', { ascending: false })
        .limit(n);
      if (error) throw new Error(error.message);
      const rows = (data ?? []) as ConditionReport[];
      const ids = [...new Set(rows.map((r) => r.by_user).filter((x): x is string => !!x))];
      const names = new Map<string, string>();
      if (ids.length) {
        const p = await supabase().from('profiles').select('user_id, full_name').in('user_id', ids);
        for (const r of (p.data ?? []) as { user_id: string; full_name: string | null }[])
          names.set(r.user_id, r.full_name ?? '');
      }
      return rows.map((r) => ({
        ...r,
        by_name: r.by_user ? (names.get(r.by_user) ?? null) : null,
      }));
    },
  });
}

function rpcError(error: { message: string; code?: string }) {
  return Object.assign(new Error(error.message), { code: error.code });
}

function useRefresh(assetId: string) {
  const queryClient = useQueryClient();
  return () => {
    void queryClient.invalidateQueries({ queryKey: conditionKeys.asset(assetId) });
    void queryClient.invalidateQueries({ queryKey: keys.asset(assetId) });
    void queryClient.invalidateQueries({ queryKey: lifecycleKeys.asset(assetId) });
    void queryClient.invalidateQueries({ queryKey: ['assets'] });
  };
}

/** Report condition: the photo (downsized) goes up first; a refused report removes it again. */
export function useReportCondition(companyId: string, assetId: string) {
  const refresh = useRefresh(assetId);
  return useMutation({
    mutationFn: async (r: { condition: AssetCondition; note: string; photo: File | null }) => {
      let path: string | null = null;
      if (r.photo) {
        const jpeg = await downscaleImage(r.photo);
        path = docPath(companyId, 'condition', crypto.randomUUID(), 'image/jpeg');
        const up = await supabase()
          .storage.from(DOC_BUCKET)
          .upload(path, jpeg, { contentType: 'image/jpeg', upsert: false });
        if (up.error) throw new Error(up.error.message);
      }
      const { error } = await supabase().rpc('report_condition', {
        p_idempotency_key: crypto.randomUUID(),
        p_asset_id: assetId,
        p_condition: r.condition,
        p_note: r.note.trim() || null,
        p_photo_path: path,
      });
      if (error) {
        if (path) await supabase().storage.from(DOC_BUCKET).remove([path]);
        throw rpcError(error);
      }
    },
    onSettled: refresh,
  });
}

export function useSetServiceState(assetId: string) {
  const refresh = useRefresh(assetId);
  return useMutation({
    mutationFn: async (r: { out: boolean; reason: string }) => {
      const { error } = await supabase().rpc('set_service_state', {
        p_idempotency_key: crypto.randomUUID(),
        p_asset_id: assetId,
        p_out: r.out,
        p_reason: r.out ? r.reason.trim() : null,
      });
      if (error) throw rpcError(error);
    },
    onSettled: refresh,
  });
}
